/**
 * Cross-tab sign-out notification.
 *
 * Signing out is a server action that clears cookies, so nothing pushes an event to the
 * other tabs this browser has open on the app. Supabase's own BroadcastChannel only
 * relays events a JS client emitted, and its token-refresh tick is what would eventually
 * notice — up to the full JWT lifetime later. Until then a sibling tab keeps rendering
 * the previous user's dashboard from its own QueryClient, which is exactly the exposure
 * this feature exists to close, just in a different tab.
 *
 * So the tab that signs out tells the others directly.
 */

const CHANNEL_NAME = 'bite-club-session';
const SIGNED_OUT = 'signed-out';

type SignOutMessage = {
  type: typeof SIGNED_OUT;
  /** Where the sibling tabs should land, so they match the role that signed out. */
  loginRoute: string;
  /** Which document sent this, so the sender can ignore its own message. */
  senderId: string;
};

/**
 * Identifies this document on the channel.
 *
 * BroadcastChannel skips only the channel OBJECT that posted, not the context. The
 * poster and the listener here are separate objects in the same tab, so without this
 * the signing-out tab would receive its own message and navigate a second time while
 * the first navigation is already in flight.
 */
const SENDER_ID = Math.random().toString(36).slice(2);

function isSupported(): boolean {
  return typeof window !== 'undefined' && typeof BroadcastChannel === 'function';
}

function isSignOutMessage(data: unknown): data is SignOutMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === SIGNED_OUT &&
    typeof (data as { loginRoute?: unknown }).loginRoute === 'string' &&
    typeof (data as { senderId?: unknown }).senderId === 'string'
  );
}

// Held open for the life of the document rather than opened per post: delivery is
// queued as a task, so closing the channel in the same tick discards the message.
let outboundChannel: BroadcastChannel | null = null;

/**
 * Tell every other tab that this session has ended.
 *
 * Call before navigating away. Delivery is queued rather than synchronous, but the
 * queued task survives this document being replaced — the receiving tabs are separate
 * contexts with their own event loops.
 */
export function broadcastSignOut(loginRoute: string): void {
  if (!isSupported()) return;

  try {
    if (!outboundChannel) {
      outboundChannel = new BroadcastChannel(CHANNEL_NAME);
    }
    const message: SignOutMessage = { type: SIGNED_OUT, loginRoute, senderId: SENDER_ID };
    outboundChannel.postMessage(message);
  } catch (error) {
    // A sibling tab failing to hear about this must never block the sign-out itself.
    outboundChannel = null;
    console.error('Failed to broadcast sign-out to other tabs:', error);
  }
}

/**
 * React to another tab signing out. Returns an unsubscribe function.
 *
 * Messages this document sent are filtered out by sender id, so the tab that signed out
 * never acts on its own broadcast.
 */
export function subscribeToSignOut(onSignOut: (loginRoute: string) => void): () => void {
  if (!isSupported()) return () => {};

  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return () => {};
  }

  const handler = (event: MessageEvent) => {
    if (!isSignOutMessage(event.data)) return;
    if (event.data.senderId === SENDER_ID) return;
    onSignOut(event.data.loginRoute);
  };

  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}
